import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/language';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem
} from '@/components/ui/dropdown-menu';
import {
  User,
  LogOut,
  Settings,
  Home,
  Search,
  Menu,
  X,
  Languages
} from 'lucide-react';
import { useAdmin } from '@/contexts/AdminContext';
import { AuthModal } from '@/components/auth/AuthModal';
import { logger } from '@/utils/logger';

interface HeaderProps {
  showAuthButtons?: boolean;
}

export const Header: React.FC<HeaderProps> = ({ showAuthButtons = true }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const { isAdmin } = useAdmin();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('signup');

  // Hide signup button on landing pages
  const isLandingPage = location.pathname === '/simulateurs' || location.pathname === '/plateforme';
  const shouldShowSignUp = showAuthButtons && !isLandingPage;

  const handleSignOut = async () => {
    setIsLoading(true);
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      logger.error('Error signing out:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartTrial = () => {
    navigate('/auth?type=trial');
  };

  const handleSignIn = () => {
    setAuthMode('login');
    setShowAuthModal(true);
  };

  // Get user's display name from onboarding data or fall back to email
  const getUserDisplayName = () => {
    const fullName = user?.user_metadata?.onboarding_data?.fullName;
    if (fullName) return fullName;
    return user?.email || 'Utilisateur';
  };

  const getUserInitials = (name?: string) => {
    const displayName = name || getUserDisplayName();

    // If it's an email, use first 2 chars of local part
    if (displayName.includes('@')) {
      return displayName.split('@')[0].substring(0, 2).toUpperCase();
    }

    // If it's a name, use first letter of first and last name
    const parts = displayName.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }

    // Single word name - use first 2 chars
    return displayName.substring(0, 2).toUpperCase();
  };

  // 🇫🇷 Use French URLs for navigation
  const navItems = user ? [
    { path: '/tableau-de-bord', label: t('navigation.dashboard'), icon: Home },
    { path: '/subventions/recherche', label: t('navigation.findFunding'), icon: Search },
  ] : [];

  return (
    <>
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex h-16 items-center justify-between px-4">
          {/* Logo - Left edge */}
          <div
            className="flex items-center space-x-2 md:space-x-3 cursor-pointer"
            onClick={() => navigate('/')}
          >
            <img
              src="/favicon.png"
              alt="subvention360"
              className="h-8 w-8"
            />
            <span className="hidden md:inline text-xl font-bold">
              subvention360
            </span>
          </div>

            {/* Spacer to push right side controls to the right */}
            <div className="flex-1" />

            {/* Right Side */}
            <div className="flex items-center space-x-4">
              {/* Navigation Icons - Only for authenticated users */}
              {user && (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigate('/tableau-de-bord')}
                    className="h-10 w-10"
                    title="Tableau de bord"
                    aria-label="Tableau de bord"
                  >
                    <Home className="h-5 w-5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigate('/subventions/recherche')}
                    className="h-10 w-10"
                    title="Trouver un financement"
                    aria-label="Trouver un financement"
                  >
                    <Search className="h-5 w-5" />
                  </Button>
                </>
              )}

              {/* Auth Buttons for Non-Authenticated Users */}
              {!user && showAuthButtons && (
                <>
                  {/* Desktop Auth Buttons */}
                  <div className="hidden md:flex items-center space-x-3">
                    <Button
                      variant="ghost"
                      onClick={handleSignIn}
                      className="font-medium"
                    >
                      {t('navigation.signIn')}
                    </Button>
                    {shouldShowSignUp && (
                      <Button
                        onClick={handleStartTrial}
                        className="font-medium"
                      >
                        {t('navigation.signUp')}
                      </Button>
                    )}
                  </div>

                  {/* Burger Menu for Mobile Only */}
                  <div className="md:hidden">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" aria-label="Ouvrir le menu">
                          <Menu className="w-5 h-5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56 bg-background z-50">
                        <DropdownMenuItem onClick={handleSignIn}>
                          <User className="mr-2 h-4 w-4" />
                          <span>{t('navigation.signIn')}</span>
                        </DropdownMenuItem>
                        {shouldShowSignUp && (
                          <DropdownMenuItem onClick={handleStartTrial}>
                            <User className="mr-2 h-4 w-4" />
                            <span>{t('navigation.signUp')}</span>
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => navigate('/product-details')}>
                          <Search className="mr-2 h-4 w-4" />
                          <span>{t('common.features')}</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/pricing')}>
                          <span className="mr-2 h-4 w-4 flex items-center justify-center">€</span>
                          <span>{t('common.pricing')}</span>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuSub>
                          <DropdownMenuSubTrigger>
                            <Languages className="mr-2 h-4 w-4" />
                            <span>{t('navigation.language')}</span>
                          </DropdownMenuSubTrigger>
                          <DropdownMenuSubContent className="bg-background">
                            <DropdownMenuRadioGroup value={language} onValueChange={setLanguage}>
                              <DropdownMenuRadioItem value="fr">
                                {t('navigation.french')}
                              </DropdownMenuRadioItem>
                              <DropdownMenuRadioItem value="en">
                                {t('navigation.english')}
                              </DropdownMenuRadioItem>
                            </DropdownMenuRadioGroup>
                          </DropdownMenuSubContent>
                        </DropdownMenuSub>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </>
              )}

              {/* User Menu for Authenticated Users */}
              {user && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="text-xs">
                          {getUserInitials()}
                        </AvatarFallback>
                      </Avatar>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56 bg-background z-50" align="end" forceMount>
                    <div className="flex flex-col space-y-1 p-2">
                      <p className="text-sm font-medium leading-none">{getUserDisplayName()}</p>
                      <p className="text-xs leading-none text-muted-foreground">{user.email}</p>
                      <p className="text-xs leading-none text-muted-foreground">
                        {isAdmin ? t('common.administrator') : t('common.businessUser')}
                      </p>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/settings')}>
                      <Settings className="mr-2 h-4 w-4" />
                      <span>{t('navigation.settings')}</span>
                    </DropdownMenuItem>
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <Languages className="mr-2 h-4 w-4" />
                        <span>{t('navigation.language')}</span>
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent className="bg-background">
                        <DropdownMenuRadioGroup value={language} onValueChange={setLanguage}>
                          <DropdownMenuRadioItem value="fr">
                            {t('navigation.french')}
                          </DropdownMenuRadioItem>
                          <DropdownMenuRadioItem value="en">
                            {t('navigation.english')}
                          </DropdownMenuRadioItem>
                        </DropdownMenuRadioGroup>
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleSignOut} disabled={isLoading}>
                      <LogOut className="mr-2 h-4 w-4" />
                      <span>{t('navigation.signOut')}</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {/* Mobile Menu Button - Removed as burger menu handles mobile now */}
            </div>
          </div>
      </header>

      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        mode={authMode}
        onModeChange={setAuthMode}
      />
    </>
  );
};